/**
 * PatrolCircleInfoWindow
 *
 * Popup component that displays patrol/site information when a patrol circle is clicked.
 * Shows site name, guard assignment, distance from site, and actions.
 *
 * This component is positioned using google-map-react's child positioning (lat/lng props).
 */

import React from 'react';
import { Box, Paper, Typography, Button, Chip, ClickAwayListener } from '@mui/material';
import PlaceIcon from '@mui/icons-material/Place';
import PersonIcon from '@mui/icons-material/Person';
import styles from './PatrolCircleInfoWindow.module.css';
import {
  PatrolLocation,
  PATROL_CIRCLE_COLORS,
  PATROL_CIRCLE_STATUS_LABELS,
  PATROL_STATUS_LABELS,
  PATROL_STATUS_COLORS,
} from './types';
import { formatDistance } from './utils';

interface PatrolCircleInfoWindowProps {
  /** Patrol location data */
  patrol: PatrolLocation;
  /** Callback when "Site Info" is clicked (opens site info modal) */
  onViewSiteDetails?: (addressId: number) => void;
  /** Callback to close the info window */
  onClose?: () => void;
  /** Required by google-map-react for positioning */
  lat: number;
  /** Required by google-map-react for positioning */
  lng: number;
}

const PatrolCircleInfoWindow: React.FC<PatrolCircleInfoWindowProps> = ({
  patrol,
  onViewSiteDetails,
  onClose,
}) => {
  const handleViewSiteDetails = (e: React.MouseEvent) => {
    e.stopPropagation();
    onViewSiteDetails?.(patrol.addressId);
  };

  const handleClickAway = () => {
    onClose?.();
  };

  const colors = PATROL_CIRCLE_COLORS[patrol.circleStatus];
  const circleStatusLabel = PATROL_CIRCLE_STATUS_LABELS[patrol.circleStatus];
  const patrolStatusLabel = PATROL_STATUS_LABELS[patrol.patrolStatus];
  const patrolStatusColor = PATROL_STATUS_COLORS[patrol.patrolStatus];

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <div className={styles.container}>
        <Paper className={styles.popup} elevation={4}>
          {/* Header */}
          <Box className={styles.popupHeader}>
            <Box
              className={styles.popupStatusDot}
              style={{ backgroundColor: colors.stroke }}
            />
            <Box className={styles.headerText}>
              <Typography variant="subtitle1" className={styles.siteName}>
                {patrol.siteName}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {patrol.customerName}
              </Typography>
            </Box>
          </Box>

          {/* Patrol Status */}
          <Box className={styles.statusRow}>
            <Chip
              label={patrolStatusLabel}
              size="small"
              sx={{
                backgroundColor: patrolStatusColor,
                color: '#fff',
                fontWeight: 500,
              }}
            />
            <Typography
              variant="body2"
              className={styles.circleStatus}
              style={{ color: colors.stroke }}
            >
              {circleStatusLabel}
            </Typography>
          </Box>

          {/* Guard Info (if assigned) */}
          {patrol.guardId && patrol.guardName && (
            <Box className={styles.guardSection}>
              <Box className={styles.popupRow}>
                <Box className={styles.rowLabel}>
                  <PersonIcon className={styles.rowIcon} />
                  <Typography variant="body2" color="textSecondary">
                    Guard:
                  </Typography>
                </Box>
                <Typography variant="body2" className={styles.popupValue}>
                  {patrol.guardName}
                </Typography>
              </Box>

              {/* Distance */}
              {patrol.guardDistance !== null && (
                <Box className={styles.popupRow}>
                  <Box className={styles.rowLabel}>
                    <PlaceIcon className={styles.rowIcon} />
                    <Typography variant="body2" color="textSecondary">
                      Distance:
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    className={styles.popupValue}
                    style={{
                      color:
                        patrol.circleStatus === 'guard_inside'
                          ? PATROL_CIRCLE_COLORS.guard_inside.stroke
                          : patrol.circleStatus === 'guard_outside'
                          ? PATROL_CIRCLE_COLORS.guard_outside.stroke
                          : undefined,
                      fontWeight:
                        patrol.circleStatus === 'guard_inside' ||
                        patrol.circleStatus === 'guard_outside'
                          ? 600
                          : 500,
                    }}
                  >
                    {formatDistance(patrol.guardDistance)}
                    {patrol.circleStatus === 'guard_inside' && ' (on site)'}
                    {patrol.circleStatus === 'guard_outside' && ' (away)'}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* No Guard Message */}
          {!patrol.guardId && patrol.patrolStatus !== 'completed' && (
            <Box className={styles.noGuardSection}>
              <Typography variant="body2" color="textSecondary">
                No guard location available
              </Typography>
            </Box>
          )}

          {/* Actions */}
          <Box className={styles.popupActions}>
            <Button
              variant="contained"
              size="small"
              fullWidth
              onClick={handleViewSiteDetails}
            >
              Site Info
            </Button>
          </Box>
        </Paper>
      </div>
    </ClickAwayListener>
  );
};

export default PatrolCircleInfoWindow;
